// @ts-check
import { area, circle, kinks, polygon, simplify } from '@turf/turf';
import LocationAnalysisType from '../enums/LocationAnalysisType';

// STAR-418; the server holds the same cap (CUSTOM_POLYGON_MAX_AREA_SQ_MI).
export const MAX_AREA_SQ_MI = 1000;
const SQ_METERS_PER_SQ_MILE = 2589988.110336;
const RADIUS_SEED_STEPS = 32;
const CONTOUR_SEED_TARGET = 50;
const SIMPLIFY_START_TOLERANCE = 0.0005;
const SIMPLIFY_MAX_DOUBLINGS = 20;
const SIMPLIFY_BISECTIONS = 12;
export const MIN_VERTICES = 3;
export const TOO_FEW_POINTS = 'A shape needs at least three points.';
const SEEDABLE_TYPES = [LocationAnalysisType.RADIUS.ID, LocationAnalysisType.DRIVING_TIME.ID];

/** @param {[number, number][]} ring unclosed `[lng, lat]` */
const closed = ring => [...ring, ring[0]];

/** @param {[number, number][]} ring unclosed */
export function ringAreaSqMi(ring) {
    if (ring.length < MIN_VERTICES) return 0;
    return area(polygon([closed(ring)])) / SQ_METERS_PER_SQ_MILE;
}

/** @param {[number, number][]} ring unclosed */
export function ringCrossings(ring) {
    return kinks(polygon([closed(ring)])).features.length;
}

/**
 * Why a ring cannot be an analysis, or null when it can.
 * @param {[number, number][]} ring unclosed
 * @returns {string | null}
 */
export function ringProblem(ring) {
    if (ring.length < MIN_VERTICES) return TOO_FEW_POINTS;
    const crossings = ringCrossings(ring);
    if (crossings === 1) return 'The outline crosses itself. Drag a point to untangle it.';
    if (crossings > 1) {
        return `The outline crosses itself in ${crossings} places. Drag points to untangle it.`;
    }
    if (ringAreaSqMi(ring) > MAX_AREA_SQ_MI) {
        return `The area is larger than ${MAX_AREA_SQ_MI.toLocaleString()} square miles.`;
    }
    return null;
}

/**
 * @param {{lng: number, lat: number}} point
 * @param {number} miles
 * @returns {[number, number][]} unclosed
 */
export function radiusSeedRing(point, miles) {
    const coordinates = circle([point.lng, point.lat], miles, {
        steps: RADIUS_SEED_STEPS,
        units: 'miles',
    }).geometry.coordinates[0];
    return coordinates.slice(0, -1);
}

/**
 * The outer ring of a contour's largest polygon, simplified to about fifty
 * vertices so the handles stay grabbable, and never crossing itself.
 * @param {any} geometry GeoJSON Polygon or MultiPolygon
 * @returns {[number, number][] | null} unclosed
 */
export function contourSeedRing(geometry) {
    if (!geometry) return null;
    const outers =
        geometry.type === 'MultiPolygon'
            ? geometry.coordinates.map(p => p[0])
            : geometry.type === 'Polygon'
            ? [geometry.coordinates[0]]
            : [];
    let outer = null;
    let largest = -1;
    outers.forEach(ring => {
        if (!ring || ring.length <= MIN_VERTICES) return;
        const size = area(polygon([ring]));
        if (size > largest) {
            largest = size;
            outer = ring;
        }
    });
    if (!outer) return null;

    const fits = ring =>
        ring.length - 1 <= CONTOUR_SEED_TARGET && !ringCrossings(ring.slice(0, -1));
    if (fits(outer)) return outer.slice(0, -1);
    const simplifyAt = tolerance => {
        try {
            const ring = simplify(polygon([outer]), { tolerance, highQuality: true }).geometry
                .coordinates[0];
            return ring.length > MIN_VERTICES ? ring : null;
        } catch (e) {
            return null;
        }
    };
    // Double the tolerance until the ring is small enough, then bisect back
    // towards the most detailed ring that still is.
    let low = 0;
    let high = SIMPLIFY_START_TOLERANCE;
    let simplified = simplifyAt(high);
    for (let i = 0; i < SIMPLIFY_MAX_DOUBLINGS && simplified && !fits(simplified); i++) {
        low = high;
        high *= 2;
        simplified = simplifyAt(high);
    }
    if (!simplified || !fits(simplified)) return null;
    for (let i = 0; i < SIMPLIFY_BISECTIONS; i++) {
        const mid = (low + high) / 2;
        const ring = simplifyAt(mid);
        if (ring && fits(ring)) {
            high = mid;
            simplified = ring;
        } else {
            low = mid;
        }
    }
    return simplified.slice(0, -1);
}

/**
 * "Start from" for the Custom shape view: the widest ring of the open radius
 * or drive-time analysis whose shape fits under the cap.
 * @param {import('../objects/LocationAnalysisItem').default} item
 * @returns {{label: string, ring: [number, number][] | null, reason: string | null} | null}
 */
export function seedFromAnalysis(item) {
    if (!item || !item.point || !item.selection || !item.selection.size) return null;
    const typeId = item.analysisTypeId;
    if (!SEEDABLE_TYPES.includes(typeId)) return null;
    const isRadius = typeId === LocationAnalysisType.RADIUS.ID;

    const values = [...item.selection].sort((a, b) => b - a);
    const label = value =>
        isRadius ? `Start from ${value} mi radius` : `Start from ${value} min drive time`;
    let widest = null;
    for (let i = 0; i < values.length; i++) {
        const value = values[i];
        let ring = null;
        if (isRadius) {
            ring = radiusSeedRing(item.point, value);
        } else {
            const byValue = item.sourceData && item.sourceData[item.contourType];
            const feature = byValue && byValue[value];
            ring = feature ? contourSeedRing(feature.geometry) : null;
        }
        if (!ring) continue;
        if (widest === null) widest = value;
        if (ringAreaSqMi(ring) <= MAX_AREA_SQ_MI) return { label: label(value), ring, reason: null };
    }
    if (widest === null) return null;
    return {
        label: label(widest),
        ring: null,
        reason: `Every ring is over ${MAX_AREA_SQ_MI.toLocaleString()} sq mi.`,
    };
}
